import { useCallback, useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import { InitiateOAuthFlow, RefreshExternalToken, VerifyAccounts } from "../graphql/externalAccountsMutations.js";
export var OAuthPermissionLevel;
(function (OAuthPermissionLevel) {
    OAuthPermissionLevel["READ"] = "read";
    OAuthPermissionLevel["WRITE"] = "write";
    OAuthPermissionLevel["READ_AND_WRITE"] = "read_and_write";
})(OAuthPermissionLevel || (OAuthPermissionLevel = {}));
export const useExternalAccounts = (options) => {
    const logErr = useMemo(() => options?.logError ?? ((...args) => console.error(...args)), [options?.logError]);
    const [initiateOAuthFlowMutation, initiateOAuthFlowResult] = useMutation(InitiateOAuthFlow, {
        onCompleted: (data) => {
            if (data.initiateOAuthFlow) {
                options?.onOAuthFlowSuccess?.(data.initiateOAuthFlow);
            }
        },
        onError: (err) => {
            logErr("[useExternalAccounts] Error initiating OAuth flow:", err);
            options?.onOAuthFlowError?.(err);
        },
    });
    const initiateOAuthFlow = useCallback(async (input) => {
        try {
            const result = await initiateOAuthFlowMutation({
                variables: { input },
            });
            if (!result.data?.initiateOAuthFlow?.authenticationUrl) {
                throw new Error("No authentication URL in response");
            }
            return result.data.initiateOAuthFlow;
        }
        catch (error) {
            logErr("[useExternalAccounts] initiateOAuthFlow failed:", error);
            throw error;
        }
    }, [initiateOAuthFlowMutation, logErr]);
    const [refreshTokenMutation, refreshTokenResult] = useMutation(RefreshExternalToken, {
        onCompleted: (data) => {
            if (data.refreshExternalToken) {
                options?.onRefreshSuccess?.(data.refreshExternalToken);
            }
        },
        onError: (err) => {
            logErr("[useExternalAccounts] Error refreshing token:", err);
            options?.onRefreshError?.(err);
        },
    });
    const refreshToken = useCallback(async (refreshTokenValue, platformId) => {
        try {
            const result = await refreshTokenMutation({
                variables: {
                    input: {
                        refreshToken: refreshTokenValue,
                        platformId,
                    },
                },
            });
            if (!result.data?.refreshExternalToken?.accessToken) {
                throw new Error("No access token in refresh response");
            }
            return result.data.refreshExternalToken;
        }
        catch (error) {
            logErr("[useExternalAccounts] refreshToken failed:", error);
            throw error;
        }
    }, [refreshTokenMutation, logErr]);
    const [verifyAccountsMutation, verifyAccountsResult] = useMutation(VerifyAccounts, {
        onCompleted: (data) => {
            if (data.verifyAccounts) {
                options?.onVerifySuccess?.(data.verifyAccounts);
            }
        },
        onError: (err) => {
            logErr("[useExternalAccounts] Error verifying accounts:", err);
            options?.onVerifyError?.(err);
        },
    });
    const verifyAccounts = useCallback(async (accountDetails) => {
        try {
            const result = await verifyAccountsMutation({
                variables: {
                    input: { accountDetails },
                },
            });
            if (!result.data?.verifyAccounts?.verifications) {
                throw new Error("No verifications in response");
            }
            return result.data.verifyAccounts;
        }
        catch (error) {
            logErr("[useExternalAccounts] verifyAccounts failed:", error);
            throw error;
        }
    }, [verifyAccountsMutation, logErr]);
    const verifyAccount = useCallback(async (accountDetail) => {
        const result = await verifyAccounts([accountDetail]);
        return result?.verifications?.[0] ?? null;
    }, [verifyAccounts]);
    const getValidAccessToken = useCallback(async (accessToken, refreshTokenValue, expiresAt, platformId) => {
        const now = Date.now();
        const bufferMs = 5 * 60 * 1000;
        if (now < expiresAt - bufferMs) {
            return { accessToken, expiresAt };
        }
        try {
            const refreshResponse = await refreshToken(refreshTokenValue, platformId);
            if (!refreshResponse) {
                logErr("[useExternalAccounts] Refresh returned null");
                return null;
            }
            const newExpiresAt = Date.now() + (refreshResponse.expiresInSeconds || 3600) * 1000;
            return {
                accessToken: refreshResponse.accessToken,
                expiresAt: newExpiresAt,
            };
        }
        catch (error) {
            logErr("[useExternalAccounts] Failed to refresh token:", error);
            return null;
        }
    }, [refreshToken, logErr]);
    return {
        initiateOAuthFlow,
        refreshToken,
        verifyAccounts,
        verifyAccount,
        getValidAccessToken,
        loading: initiateOAuthFlowResult.loading || refreshTokenResult.loading || verifyAccountsResult.loading,
        initiateOAuthFlowLoading: initiateOAuthFlowResult.loading,
        refreshTokenLoading: refreshTokenResult.loading,
        verifyAccountsLoading: verifyAccountsResult.loading,
        error: initiateOAuthFlowResult.error || refreshTokenResult.error || verifyAccountsResult.error,
        initiateOAuthFlowError: initiateOAuthFlowResult.error,
        refreshTokenError: refreshTokenResult.error,
        verifyAccountsError: verifyAccountsResult.error,
    };
};
